/**
 * Email Promo Code Selector Component
 *
 * Renders promo codes as a list for email campaigns.
 * Inserts actual promo code values (not {{promoCode}} variable).
 *
 * Phase 1, Task 1.4 (Updated for modal use)
 * Date: 2025-10-21
 */

import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tag, AlertCircle } from 'lucide-react';
import type { PromoCode } from '@/hooks/sms/types';
import { usePromoCodesManager } from '@/hooks/sms/usePromoCodesManager';
import { useAuth } from '@/context/auth';

interface EmailPromoCodeSelectorProps {
  /** Restaurant ID to fetch promo codes for */
  restaurantId?: string;

  /** Callback when promo code is selected */
  onCodeSelect: (code: string, codeId: number, promoDetails?: PromoCode) => void;

  /** Disabled state */
  disabled?: boolean;

  /** Current email content (to show which codes are already used) */
  currentEmailContent?: string;
}

/**
 * Email Promo Code Selector
 *
 * Renders as a list of buttons (not a dropdown) to avoid overlay conflicts in modals.
 * Promo codes insert the actual code value (e.g., "SAVE20"), not a variable.
 *
 * @example
 * <EmailPromoCodeSelector
 *   restaurantId={selectedRestaurantId}
 *   onCodeSelect={(code, id, details) => {
 *     insertIntoEmail(code); // Insert "SAVE20"
 *     updateCampaignPromoCode(id); // Track for attribution
 *   }}
 *   currentEmailContent={emailContent}
 * />
 */
const EmailPromoCodeSelector: React.FC<EmailPromoCodeSelectorProps> = ({
  restaurantId,
  onCodeSelect,
  disabled = false,
  currentEmailContent = ''
}) => {
  const { user } = useAuth();

  // Memoize the selectedRestaurants array to prevent infinite re-renders
  const selectedRestaurants = useMemo(() => {
    return restaurantId ? [restaurantId] : [];
  }, [restaurantId]);

  const { promoCodes, isLoading, error } = usePromoCodesManager(user, selectedRestaurants);

  const formatDiscount = (promoCode: PromoCode) => {
    switch (promoCode.discount_type) {
      case 'percentage_off':
        return `${promoCode.discount_value}% off`;
      case 'dollar_off':
        return `$${promoCode.discount_value} off`;
      case 'free_item':
        return 'Free item';
      default:
        return 'Discount';
    }
  };

  // Filter to only active codes and check if they're not expired
  const activeCodes = promoCodes.filter(code => {
    if (!code.active) return false;

    const now = new Date();
    if (code.end_date && new Date(code.end_date) < now) return false;
    if (code.start_date && new Date(code.start_date) > now) return false;

    return true;
  });

  const isCodeInMessage = (code: string) => {
    return currentEmailContent.includes(code);
  };

  if (!restaurantId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please select a restaurant first to view promo codes.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Error loading promo codes. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (activeCodes.length === 0) {
    return (
      <Alert>
        <Tag className="h-4 w-4" />
        <AlertDescription>
          No active promo codes available for this restaurant.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      {activeCodes.map((code) => (
        <Button
          key={code.id}
          variant="outline"
          size="sm"
          onClick={() => onCodeSelect(code.code, code.id, code)}
          disabled={disabled}
          className="w-full justify-start text-left h-auto py-3"
        >
          <div className="flex items-start gap-3 w-full">
            <Tag className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium text-sm">{code.code}</span>
                <Badge variant="secondary" className="text-xs">
                  {formatDiscount(code)}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground text-left">
                {code.name || `${code.discount_type.replace('_', ' ')} discount`}
              </p>
              {code.usage_limit && (
                <p className="text-xs text-muted-foreground text-left">
                  Used: {code.current_usage || 0}/{code.usage_limit}
                </p>
              )}
              {isCodeInMessage(code.code) && (
                <Badge variant="default" className="text-xs">
                  ✓ Added to email
                </Badge>
              )}
            </div>
          </div>
        </Button>
      ))}
    </div>
  );
};

export default EmailPromoCodeSelector;
